<template>
    <div :data-discussion-key="messageKey" :data-discussion-source="sourceKey" class="discussion-text" :class="{'discussion-text--reply': message && message.reply}" tabindex="-1">
        <button v-if="message && message.reply" class="discussion-text__reply" type="button" title="Перейти к исходному сообщению" @click="jump(message.reply.message_ref, message.reply)">
            <strong>{{ message.reply.author_name }}</strong>
            <span v-if="message.reply.deleted">Комментарий удалён</span>
            <template v-else><blockquote class="user-quote">{{ message.reply.quote }}</blockquote><small v-if="message.reply.edited">Изменено</small></template>
        </button>
        <blockquote ref="body" class="user-quote discussion-text__body" @mouseup="inspectSelection">
            <template v-for="(part, index) in parts">
                <span :key="index" :class="{'discussion-text__quoted-fragment': part.highlighted}"><EmployeeLink v-if="part.user_id" :user-id="part.user_id" :fallback="part.name" name-order="last-first" compact>{{ part.text }}</EmployeeLink><span v-else v-html="linkify(part.text)"></span></span>
            </template>
        </blockquote>
        <div v-if="message && !message.unavailable && message.can_reply" class="discussion-text__actions" role="group" aria-label="Ответ на комментарий">
            <button type="button" @click="replyWhole">Ответить</button>
            <button v-if="selection" type="button" @click="replySelection">Ответить на цитату</button>
            <button type="button" @click="quoteOpen = true">Выбрать цитату</button>
        </div>
        <div v-if="error" role="alert" class="small text-danger">{{ error }} <button type="button" @click="load">Повторить</button></div>
        <b-modal v-model="quoteOpen" title="Выбрать цитату" hide-footer content-class="argus-glass-dialog">
            <p class="small text-muted">Выделите нужную часть текста и нажмите «Ответить на цитату».</p>
            <textarea ref="quote" class="form-control" :value="value" readonly rows="7" @select="selectQuote" @touchend="selectQuote" @keyup="selectQuote"></textarea>
            <div class="mt-3 d-flex justify-content-end">
                <b-button variant="outline-secondary" class="mr-2" @click="quoteOpen = false">Отмена</b-button>
                <b-button variant="primary" :disabled="!selection" @click="replySelection">Ответить на цитату</b-button>
            </div>
        </b-modal>
    </div>
</template>
<script>
import EmployeeLink from '@/components/EmployeeLink.vue';
import linkify from '@/utils/Urlify';
import {readMessage, replyTo, discussionContextMixin, discussionEvents} from '@/utils/discussions';
const {messageKey, textParts} = require('../../../../shared/discussionText');
export default {
    name: 'DiscussionText', mixins: [discussionContextMixin], components: {EmployeeLink},
    props: {value: {type: String, default: ''}, sourceType: String, sourceId: [String, Number], authorId: [String, Number], fieldKey: {type: String, default: 'body'}, supplied: Object},
    data: () => ({message: null, error: '', version: 0, selection: null, quoteOpen: false, highlightedQuote: null}),
    computed: {
        reference() { return this.currentContext && this.sourceType && this.sourceId ? {context_type: this.currentContext.type, context_id: Number(this.currentContext.id), source_type: this.sourceType, source_id: Number(this.sourceId), field_key: this.fieldKey} : null; },
        messageKey() { return messageKey(this.message && this.message.message_ref || this.reference); },
        sourceKey() { return messageKey(this.message && this.message.message_ref || this.reference, true); },
        parts() {
            const mentions = this.message && this.message.body === this.value ? this.message.mentions || [] : [];
            return textParts(this.value, mentions, this.highlightedQuote);
        }
    },
    watch: {reference: {deep: true, immediate: true, handler() { this.load(); }}, currentContext: {deep: true, handler() { this.load(); }}, value() { this.load(); }},
    mounted() {
        this.highlightListener = (ref, quote) => { this.highlightedQuote = ref && messageKey(ref) === this.messageKey ? quote : null; };
        discussionEvents.$on('highlight-quote', this.highlightListener);
    },
    beforeDestroy() { this.version++; discussionEvents.$off('highlight-quote', this.highlightListener); },
    methods: {
        linkify,
        async load() {
            const version = ++this.version; this.message = null; this.error = ''; this.selection = null;
            if (!this.currentContext || this.currentContext.id === 'new' || !this.value) return;
            try { const message = this.supplied || await readMessage(this.reference, this.value, this.currentContext, this.authorId); if (!this._isDestroyed && version === this.version) this.message = message; }
            catch (e) { if (version === this.version) this.error = 'Не удалось загрузить связь сообщения.'; }
        },
        inspectSelection() {
            const selection = window.getSelection(), body = this.$refs.body;
            this.selection = null;
            if (!selection || !selection.rangeCount || selection.isCollapsed) return;
            const range = selection.getRangeAt(0);
            if (!body.contains(range.startContainer) || !body.contains(range.endContainer)) return;
            const prefix = range.cloneRange(); prefix.selectNodeContents(body); prefix.setEnd(range.startContainer, range.startOffset);
            const start = prefix.toString().length, text = range.toString();
            if (this.value.slice(start, start + text.length) === text) this.selection = {start, length: text.length};
        },
        selectQuote() {
            this.$nextTick(() => { const input = this.$refs.quote; if (!input) return; const length = input.selectionEnd - input.selectionStart; this.selection = length > 0 ? {start: input.selectionStart, length} : null; });
        },
        replyWhole() { if (this.message) replyTo(this.currentContext, this.message, undefined, undefined, this.$el); },
        replySelection() { if (!this.selection || !this.message) return; replyTo(this.currentContext, this.message, this.selection.start, this.selection.length, this.$el); this.quoteOpen = false; },
        jump(ref, quote) { discussionEvents.$emit('jump', ref, quote); }
    }
};
</script>
<style scoped>
.discussion-text { min-width: 0; overflow-wrap: anywhere; }
.discussion-text--reply { border-radius: 13px; padding: 9px 11px 5px; background: #f1f7fb; border: 1px solid #e0edf5; margin: 7px 0; }
.discussion-text__body { white-space: pre-wrap; margin: 4px 0; }
.discussion-text__quoted-fragment { background: #ffe18a; color: #263d50; border-radius: 3px; box-shadow: 0 0 0 2px #ffe18a; box-decoration-break: clone; -webkit-box-decoration-break: clone; }
.discussion-text--reply .discussion-text__body.user-quote { border: 0; padding: 4px 0; background: none; }
.discussion-text__reply { display: block; width: 100%; text-align: left; border: 0; border-left: 3px solid #459cd4; border-radius: 4px 8px 8px 4px; background: #e4f0f8; padding: 6px 9px; color: #425971; }
.discussion-text__reply:hover { background: #d9eaf6; }
.discussion-text__reply strong { display: block; color: #1976b6; font-size: 13px; }
.discussion-text__reply small { display: block; color: #71889a; font-size: 11px; }
.discussion-text__reply blockquote.user-quote { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; white-space: pre-wrap; margin: 2px 0 0; border: 0; padding: 0; background: none; font-size: 13px; }
.discussion-text__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 14px;
    margin-top: 5px;
}
.discussion-text__actions button {
    max-width: 100%;
    border: 0;
    background: none;
    color: #32658f;
    padding: 4px 0;
    font-size: 12px;
    line-height: 1.4;
    cursor: pointer;
}
.discussion-text__actions button:hover { text-decoration: underline; }
.discussion-text--reply .discussion-text__actions { gap: 6px; }
.discussion-text--reply .discussion-text__actions button {
    border: 1px solid #dce5ed;
    border-radius: 6px;
    background: #f5f8fb;
    padding: 4px 8px;
}
.discussion-text--reply .discussion-text__actions button:hover { background: #eaf2f8; border-color: #b9cfe2; text-decoration: none; }
.discussion-text--reply .discussion-text__actions button:active { background: #dfeaf3; }
.discussion-text__actions button:focus-visible { outline: 2px solid #2475b5; outline-offset: 2px; }
.discussion-text:focus-within { outline-offset: 3px; }
</style>
